// src/stores/userStore.js
import { create } from 'zustand';
import * as userService from '../services/userService';
import useUIStore from './uiStore';

const useUserStore = create((set, get) => ({
  // State
  user: null,
  users: [],
  totalUsers: 0,
  isLoading: false,
  criticalError: null,        // Only for user loading failures
  operationError: null,       // For non-critical operations
  loading: false,
  selectedUser: null,
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
    totalItems: 0
  },
  filters: {
    search: '',
    role: 'all',
    clientId: 'all',
    status: 'all'
  },
  // Authentication status
  isAuthenticated: false,
  isInitialized: false,
 
  
  // Actions
    // Fetch users by client
    fetchClientUsers: async (clientId, params = {}) => {
      set({ isLoading: true, criticalError: null });
      try {
        const response = await userService.getUsersByClient(clientId, params);
        set({
          users: response.data.users,
          totalUsers: response.meta.totalCount,
          meta: response.meta,
          isLoading: false
        });
        return response.data.users;
      } catch (error) {
        set({
          isLoading: false,
          criticalError: error.message || 'Failed to fetch client users'
        });
        console.error('Error fetching client users:', error);
        // Don't throw error - return empty data
        return [];
      }
    },
  fetchUsers: async (params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await userService.getUsers(params);
      set({ 
        users: response.data.users,
        totalUsers: response.meta.total,
        meta: response.meta,
        isLoading: false 
      });
      return response.data.users;
    } catch (error) {
      set({ 
        isLoading: false,
        criticalError: error.message || 'Failed to fetch users' 
      });
     console.log('error', error);
     // Don't throw error - return empty data
     return [];
    }
  },
  
  fetchUserById: async (id) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await userService.getUserById(id);
      // Update the users array if the user is not in the array or needs updating
      const userExists = get().users.some(user => user._id === id);
      
      if (!userExists) {
        set(state => ({
          users: [...state.users, response.data.user]
        }));
      } else {
        set(state => ({
          users: state.users.map(user => 
            user._id === id ? response.data.user : user
          )
        }));
      }
      
      set({ isLoading: false, selectedUser: response.data.user });
      return response.data.user;
    } catch (error) {
      set({ 
        isLoading: false,
        criticalError: error.message || 'Failed to fetch user details' 
      });
     console.log('error', error);
     throw error;
    }
  },
  
  createUser: async (userData) => {
    set({ isLoading: true, operationError: null });
    try {
      if(!userData?.clientId || userData?.clientId === ''){
        delete userData.clientId;
      }
      const response = await userService.createUser(userData);
      set(state => ({
        users: [...state.users, response.data],
        totalUsers: state.totalUsers + 1,
        isLoading: false
      }));
      
      // Success notification
      useUIStore.getState().addNotification({
        message: 'User created successfully',
        type: 'success'
      });
      
      return response.data;
    } catch (error) {
      set({ 
        isLoading: false,
        operationError: error.message || 'Failed to create user' 
      });
      
      // Error notification
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to create user',
        type: 'error'
      });
      
      console.log('error', error);
      throw error;
    }
  },
  
  updateUser: async (id, userData) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await userService.updateUser(id, userData);
      
      // Update current user if it's the one being updated
      if (get().user && get().user._id === id) {
        set({ user: response.data });
      }
      
      // Update in users array
      set(state => ({
        users: state.users.map(user => 
          user._id === id ? response.data : user
        ),
        isLoading: false
      }));
      
      // Success notification
      useUIStore.getState().addNotification({
        message: 'User updated successfully',
        type: 'success'
      });
      
      return response.data;
    } catch (error) {
      set({ 
        isLoading: false,
        operationError: error.message || 'Failed to update user' 
      });
      
      // Error notification
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to update user',
        type: 'error'
      });
      
      console.log('error', error);
      throw error;
    }
  },
  
  deleteUser: async (id) => {
    set({ isLoading: true, operationError: null });
    try {
      await userService.deleteUser(id);
      
      set(state => ({
        users: state.users.filter(user => user._id !== id),
        totalUsers: state.totalUsers - 1,
        isLoading: false
      }));
      
      // Success notification
      useUIStore.getState().addNotification({
        message: 'User deleted successfully',
        type: 'success'
      });
      
      return true;
    } catch (error) {
      set({ 
        isLoading: false,
        operationError: error.message || 'Failed to delete user' 
      });
      
      // Error notification
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to delete user',
        type: 'error'
      });
      
      console.log('error', error);
      throw error;
    }
  },
  
  // Authentication actions
  login: async (email, password) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await userService.login(email, password);
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false
      });
      
      // Store authentication token
      localStorage.setItem('token', response.data.token);
      
      return response.data.user;
    } catch (error) {
      set({ 
        isLoading: false,
        operationError: error.message || 'Login failed' 
      });
      
      // Error notification
      useUIStore.getState().addNotification({
        message: error.message || 'Login failed',
        type: 'error'
      });
      
     console.log('error', error);
     throw error;
    }
  },
  
  logout: async () => {
    try {
      // Call logout API if needed
      await userService.logout();
    } catch (error) {
      console.log('Logout error:', error);
    } finally {
      // Always clear local state regardless of API response
      localStorage.removeItem('token');
      set({
        user: null,
        isAuthenticated: false
      });
    }
  },
  
  // Initialize auth state from localStorage token
  initialize: async () => {
    set({ isLoading: true });
    
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Fetch current user data
        const response = await userService.getCurrentUser();
        set({
          user: response.data,
          isAuthenticated: true,
          isLoading: false,
          isInitialized: true
        });
      } else {
        set({
          isLoading: false,
          isInitialized: true
        });
      }
    } catch (error) {
      console.log('Auth initialization error:', error);
      // Clear invalid token
      localStorage.removeItem('token');
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true
      });
    }
  },
  
  // Permission check
  hasPermission: (resource, action) => {
    const { user } = get();
    
    if (!user || !user.permissions) {
      return false;
    }
    
    // If user is admin/superadmin, they have all permissions
    if (user.role === 'admin' || user.role === 'superadmin') {
      return true;
    }
    
    // Check specific permissions
    return user.permissions.some(permission => 
      permission.resource === resource && 
      (permission.actions.includes(action) || permission.actions.includes('*'))
    );
  },
  
  // Clear errors
  clearError: () => set({ criticalError: null, operationError: null }),
    // Actions
    setFilters: (filters) => set({ filters: { ...get().filters, ...filters } }),
  
    resetFilters: () => set({ 
      filters: {
        search: '',
        role: 'all',
        clientId: 'all',
        status: 'all'
      } 
    }),
  
    
    updateUserPermissions: async (userId, permissions) => {
      set({ loading: true, operationError: null });
      try {
        const response = await userService.updateUserPermissions(userId, permissions);
        
        // Update the selected user if it's the one being edited
        if (get().selectedUser && get().selectedUser._id === userId) {
          set({ selectedUser: response.data.user });
        }
        
        set({ loading: false });
        
        // Success notification
        useUIStore.getState().addNotification({
          message: 'User permissions updated successfully',
          type: 'success'
        });
        
        return response.data.user;
      } catch (error) {
        set({ operationError: error.message, loading: false });
        
        // Error notification
        useUIStore.getState().addNotification({
          message: error.message || 'Failed to update permissions',
          type: 'error'
        });
        
       console.log('error', error);
       throw error;
      }
    },
    
    updateUserStatus: async (userId, active) => {
      set({ loading: true, operationError: null });
      try {
        const response = await userService.updateUserStatus(userId, active);
        
        // Update the selected user if it's the one being edited
        if (get().selectedUser && get().selectedUser._id === userId) {
          set({ selectedUser: response.data.user });
        }
        
        // Refresh users list
        get().fetchUsers(get().pagination.page, get().pagination.limit);
        
        set({ loading: false });
        
        // Success notification
        useUIStore.getState().addNotification({
          message: `User ${active ? 'activated' : 'deactivated'} successfully`,
          type: 'success'
        });
        
        return response.data.user;
      } catch (error) {
        set({ operationError: error.message, loading: false });
        
        // Error notification
        useUIStore.getState().addNotification({
          message: error.message || 'Failed to update user status',
          type: 'error'
        });
        
       console.log('error', error);
       throw error;
      }
    },
    
    assignUserToClients: async (userId, clientIds) => {
      set({ loading: true, operationError: null });
      try {
        const response = await userService.assignUserToClients(userId, clientIds);
        
        // Update the selected user if it's the one being edited
        if (get().selectedUser && get().selectedUser._id === userId) {
          set({ selectedUser: response.data.user });
        }
        
        set({ loading: false });
        
        // Success notification
        useUIStore.getState().addNotification({
          message: 'User assigned to clients successfully',
          type: 'success'
        });
        
        return response.data.user;
      } catch (error) {
        set({ operationError: error.message, loading: false });
        
        // Error notification
        useUIStore.getState().addNotification({
          message: error.message || 'Failed to assign user to clients',
          type: 'error'
        });
        
       console.log('error', error);
       throw error;
      }
    },
    
    clearSelectedUser: () => set({ selectedUser: null })
}));

export default useUserStore;